// src/pages/FormListing.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    ContentCopy as DuplicateIcon,
    AddCircle as AddIcon,
    Share as ShareIcon,
    Close as CloseIcon,
    FileCopy as CopyIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import AdminLayout from '../pages/AdminLayout';
import { collection, getDocs, updateDoc, deleteDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';

export default function FormListing() {
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchForms();
    }, []);

    const fetchForms = async () => {
        try {
            setLoading(true);
            const formsCollection = collection(db, 'forms');
            const formsSnapshot = await getDocs(formsCollection);
            const formsData = formsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setForms(formsData);
            setError('');
        } catch (err) {
            console.error('Error fetching forms:', err);
            setError('Failed to load forms. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ✅ FIXED: Added absolute path with /admin/ prefix
    const handleEditForm = (formId, formType) => {
        navigate(`/admin/forms/${formType}/${formId}`);
    };

    const handleDeleteForm = async (formId) => {
        if (!window.confirm('Are you sure you want to delete this form? This action cannot be undone.')) {
            return;
        }
        try {
            await deleteDoc(doc(db, 'forms', formId));
            setForms(forms.filter(f => f.id !== formId));
            setError('');
        } catch (err) {
            console.error('Error deleting form:', err);
            setError('Failed to delete form');
        }
    };

    const handleToggleStatus = async (form) => {
        try {
            const newStatus = form.status === 'active' ? 'inactive' : 'active';
            await updateDoc(doc(db, 'forms', form.id), {
                status: newStatus,
                updatedAt: serverTimestamp()
            });
            setForms(forms.map(f => f.id === form.id ? { ...f, status: newStatus } : f));
        } catch (err) {
            console.error('Error updating form:', err);
            setError('Failed to update form status');
        }
    };

    const handleLockForm = async (form) => {
        try {
            const newLocked = !form.isLocked;
            await updateDoc(doc(db, 'forms', form.id), {
                isLocked: newLocked,
                updatedAt: serverTimestamp()
            });
            setForms(forms.map(f => f.id === form.id ? { ...f, isLocked: newLocked } : f));
        } catch (err) {
            console.error('Error locking form:', err);
            setError('Failed to lock form');
        }
    };

    const handleDuplicateForm = async (form) => {
        try {
            const newForm = {
                ...form,
                title: `${form.title} (Copy)`,
                createdAt: serverTimestamp(),
                isLocked: false,
                status: 'inactive'
            };
            delete newForm.id;
            delete newForm.updatedAt;

            const docRef = await addDoc(collection(db, 'forms'), newForm);
            setForms([...forms, { id: docRef.id, ...newForm }]);
        } catch (err) {
            console.error('Error duplicating form:', err);
            setError('Failed to duplicate form');
        }
    };

    const getFormTypeLabel = (type) => {
        const types = {
            'evaluation': 'Evaluation',
            'input-detail': 'Input Detail',
            'feedback': 'Feedback',
            'survey': 'Survey'
        };
        return types[type] || type;
    };

    const getStatusColor = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-800 border-green-300',
            'inactive': 'bg-gray-100 text-gray-800 border-gray-300',
            'locked': 'bg-red-100 text-red-800 border-red-300'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate?.() || new Date(timestamp);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
            return 'N/A';
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const filteredForms = forms.filter(form => {
        const matchesSearch = form.title?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || form.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-8">
                <div className="text-center">
                    <div className="inline-block">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
                        <p className="text-gray-600 font-semibold">Loading forms...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <AdminLayout title="Form Management">
            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-8">
                <div className="max-w-7xl mx-auto">
                    {/* Error Alert */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center animate-fade-in shadow-lg">
                            <span className="font-medium">{error}</span>
                            <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold text-xl">×</button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-2">📋 Form Management</h1>
                            <p className="text-gray-600">Manage, edit, and share your forms</p>
                        </div>
                        <button
                            onClick={() => navigate('/admin/forms/create')}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
                        >
                            <AddIcon className="text-xl" />
                            Create New Form
                        </button>
                    </div>

                    {/* Search & Filter Bar */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        <input
                            type="text"
                            placeholder="Search forms by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="col-span-1 md:col-span-2 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
                        />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="locked">Locked</option>
                        </select>
                    </div>

                    {/* Forms Grid - Table for desktop */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                        {filteredForms.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                                        <tr>
                                            <th className="px-6 py-4 text-left font-bold text-gray-700">Form Name</th>
                                            <th className="px-6 py-4 text-left font-bold text-gray-700">Type</th>
                                            <th className="px-6 py-4 text-left font-bold text-gray-700">Status</th>
                                            <th className="px-6 py-4 text-center font-bold text-gray-700">Questions</th>
                                            <th className="px-6 py-4 text-left font-bold text-gray-700">Created</th>
                                            <th className="px-6 py-4 text-center font-bold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {filteredForms.map((form, idx) => (
                                            <tr
                                                key={form.id}
                                                className={`transition-colors duration-200 ${idx % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-100'
                                                    }`}
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                                                            {form.title?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-gray-900 truncate">{form.title}</p>
                                                            <p className="text-xs text-gray-500">ID: {form.id?.slice(0, 8)}...</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full border border-blue-200">
                                                        {getFormTypeLabel(form.formType)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(form.status)}`}>
                                                        {form.status?.charAt(0).toUpperCase() + form.status?.slice(1)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg">
                                                        {form.questions?.length || 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    {formatDate(form.createdAt)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-2 justify-center">
                                                        {/* ✅ Edit Button - FIXED */}
                                                        <button
                                                            onClick={() => handleEditForm(form.id, form.formType)}
                                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                                                            title="Edit"
                                                        >
                                                            <EditIcon className="text-xl" />
                                                        </button>

                                                        {/* Lock/Unlock Button */}
                                                        <button
                                                            onClick={() => handleLockForm(form)}
                                                            className={`p-2 rounded-lg transition-colors duration-200 ${form.isLocked ? 'text-red-600 hover:bg-red-100' : 'text-gray-600 hover:bg-gray-100'
                                                                }`}
                                                            title={form.isLocked ? 'Unlock' : 'Lock'}
                                                        >
                                                            {form.isLocked ? <LockIcon className="text-xl" /> : <LockOpenIcon className="text-xl" />}
                                                        </button>

                                                        {/* Duplicate Button */}
                                                        <button
                                                            onClick={() => handleDuplicateForm(form)}
                                                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                                                            title="Duplicate"
                                                        >
                                                            <DuplicateIcon className="text-xl" />
                                                        </button>

                                                        {/* Share Button */}
                                                        <button
                                                            onClick={() => {
                                                                setSelectedForm(form);
                                                                setShareDialogOpen(true);
                                                            }}
                                                            className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors duration-200"
                                                            title="Share"
                                                        >
                                                            <ShareIcon className="text-xl" />
                                                        </button>

                                                        {/* Delete Button */}
                                                        <button
                                                            onClick={() => handleDeleteForm(form.id)}
                                                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                                                            title="Delete"
                                                        >
                                                            <DeleteIcon className="text-xl" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="px-6 py-16 text-center">
                                <div className="text-6xl mb-4">📝</div>
                                <p className="text-gray-600 text-lg font-medium mb-4">
                                    {searchTerm || filterStatus !== 'all'
                                        ? 'No forms match your search criteria'
                                        : 'No forms created yet'}
                                </p>
                                <button
                                    onClick={() => navigate('/admin/forms/create')}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
                                >
                                    <AddIcon />
                                    Create Your First Form
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Stats */}
                    {forms.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                                <p className="text-gray-600 text-sm font-medium mb-2">Active Forms</p>
                                <p className="text-3xl font-bold text-green-600">
                                    {forms.filter(f => f.status === 'active').length}
                                </p>
                            </div>
                            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
                                <p className="text-gray-600 text-sm font-medium mb-2">Total Forms</p>
                                <p className="text-3xl font-bold text-yellow-600">{forms.length}</p>
                            </div>
                            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
                                <p className="text-gray-600 text-sm font-medium mb-2">Locked Forms</p>
                                <p className="text-3xl font-bold text-red-600">
                                    {forms.filter(f => f.isLocked).length}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Share Modal Dialog */}
            {shareDialogOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-scale-in">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Share Form</h2>
                            <button
                                onClick={() => setShareDialogOpen(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <CloseIcon className="text-2xl" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="space-y-4">
                            <p className="text-gray-600 font-medium">Share this form with others</p>
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-center gap-3">
                                <input
                                    type="text"
                                    readOnly
                                    value={`${window.location.origin}/form/${selectedForm?.id}`}
                                    className="flex-1 bg-transparent text-gray-700 font-mono text-sm focus:outline-none"
                                />
                                <button
                                    onClick={() => copyToClipboard(`${window.location.origin}/form/${selectedForm?.id}`)}
                                    className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                                    title="Copy link"
                                >
                                    <CopyIcon className="text-xl" />
                                </button>
                            </div>
                            {copied && (
                                <p className="text-green-600 font-medium text-sm animate-fade-in">
                                    ✓ Link copied to clipboard!
                                </p>
                            )}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-blue-800 text-sm">
                                    <strong>Tip:</strong> Share this link with others to let them fill out your form.
                                </p>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <button
                            onClick={() => setShareDialogOpen(false)}
                            className="w-full mt-6 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-200"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Global Styles */}
            <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-in-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-in-out;
        }
      `}</style>
        </AdminLayout>
    );
}
